import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { useSelector } from 'react-redux';
import { getGoogleMapsLoaderConfig } from '../config/googleMapsLoaderConfig';

// Context
const GoogleMapsContext = createContext(null);

/**
 * [GLOBAL PROVIDER] Google Maps Singleton Loader
 * Wraps the application to ensure the Maps API is loaded ONCE and ONLY ONCE via @googlemaps/js-api-loader.
 * Eliminates monolithic @react-google-maps/api bundle from initial main.js.
 */
export const GoogleMapsProvider = ({ children }) => {
    const settings = useSelector((state) => state.settingsdata.settings);
    const [isLoaded, setIsLoaded] = useState(() => {
        return typeof window !== 'undefined' && !!(window.google && window.google.maps);
    });
    const [loadError, setLoadError] = useState(null);

    // Derive stable config from settings
    const loaderConfig = useMemo(() => {
        return getGoogleMapsLoaderConfig(settings);
    }, [settings]);

    useEffect(() => {
        if (typeof window !== 'undefined' && window.google && window.google.maps) {
            setIsLoaded(true);
            return;
        }

        if (!loaderConfig || !loaderConfig.googleMapsApiKey) {
            return;
        }

        let isMounted = true;

        try {
            const loader = new Loader({
                id: loaderConfig.id || "google-map-script",
                apiKey: loaderConfig.googleMapsApiKey,
                version: loaderConfig.version || "weekly",
                libraries: loaderConfig.libraries || ["places"],
                language: loaderConfig.language || "fr",
                region: loaderConfig.region || "FR",
                mapIds: loaderConfig.mapIds || [],
            });

            loader.load()
                .then(() => {
                    if (isMounted) {
                        setIsLoaded(true);
                    }
                })
                .catch((err) => {
                    if (isMounted) {
                        console.error("[GoogleMapsProvider] Load error:", err);
                        setLoadError(err);
                    }
                });
        } catch (e) {
            console.error("[GoogleMapsProvider] Loader init error:", e);
            if (isMounted) setLoadError(e);
        }

        return () => {
            isMounted = false;
        };
    }, [loaderConfig]);

    // Exposed State matching the existing useGoogleMaps contract
    const contextValue = useMemo(() => {
        if (!loaderConfig) {
            return { isLoaded: false, loadError: null, apiKeyPresent: false };
        }

        return {
            isLoaded,
            loadError,
            apiKeyPresent: true,
            language: loaderConfig.language,
            region: loaderConfig.region
        };
    }, [isLoaded, loadError, loaderConfig]);

    return (
        <GoogleMapsContext.Provider value={contextValue}>
            {children}
        </GoogleMapsContext.Provider>
    );
};

/**
 * [HOOK] Consumer for Components
 * Replaces local useJsApiLoader calls with stable context.
 */
export const useGoogleMaps = () => {
    const context = useContext(GoogleMapsContext);
    if (!context) {
        throw new Error("useGoogleMaps must be used within a GoogleMapsProvider");
    }
    return context;
};
